import { useEffect, useRef, useState } from 'react'
import SignaturePad from '../pages/SignaturePad'
import { supabase } from '../lib/supabase'

const ROLE_COLOR = {
  taxpayer: { border: '#2563EB', bg: 'rgba(37,99,235,0.10)', label: '#1E40AF' },
  spouse:   { border: '#059669', bg: 'rgba(5,150,105,0.10)', label: '#065F46' },
  client:   { border: '#CBB17B', bg: 'rgba(201,168,76,0.12)', label: '#765B2C' }
}
const ROLE_TITLE = { taxpayer: 'Taxpayer', spouse: 'Spouse', client: 'Client' }
const PRELOAD_AHEAD = 2

async function authedFetch(path, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
    body: JSON.stringify(body)
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`)
  return json
}

export default function ClientSignViewer(props) {
  return <SigningSession key={props.doc.id} {...props} />
}

function SigningSession({ doc, onComplete, onClose }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [priorSigs, setPriorSigs] = useState({})
  const [visiblePages, setVisiblePages] = useState({})
  const submitLock = useRef(false)
  const [pages, setPages] = useState({})
  const [pageCount, setPageCount] = useState(0)
  const [fields, setFields] = useState([])
  const [sigs, setSigs] = useState({})
  const [padRole, setPadRole] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const pageRefs = useRef({})
  const loadingPages = useRef(new Set())          // in-flight requests
  const loadedPages = useRef(new Set())           // completed pages (ref, not state — observer closures are stale)
  const today = new Date()
  const todayStr = `${String(today.getMonth()+1).padStart(2,'0')}/${String(today.getDate()).padStart(2,'0')}/${today.getFullYear()}`

  useEffect(() => { init() }, [doc?.id])

  async function init() {
    setLoading(true); setError('')
    try {
      const { data: f, error: fErr } = await supabase
        .from('document_signature_fields')
        .select('*').eq('document_id', doc.id).eq('confirmed', true).order('page_number')
      if (fErr) throw new Error(fErr.message)
      if (!f?.some(field => field.field_type === 'signature')) throw new Error('This document has no signature fields. Please ask your firm to prepare it for signing.')
      if (f.some(field => !['signature', 'date'].includes(field.field_type) && !(['text', 'phone'].includes(field.field_type) && field.field_value))) {
        throw new Error('This document has fields that need to be corrected by your firm before you can sign.')
      }
      const { data: saved, error: savedError } = await supabase.from('document_signatures')
        .select('signer_role, signature_data, signed_at').eq('document_id', doc.id)
      if (savedError) throw new Error(savedError.message)
      setPriorSigs(Object.fromEntries((saved || []).map(row => [row.signer_role, row])))
      setFields(f)
      await loadPage(1)
      setLoading(false)
    } catch (err) { setError(err.message); setLoading(false) }
  }

  async function loadPage(p) {
    if (loadedPages.current.has(p) || loadingPages.current.has(p)) return
    loadingPages.current.add(p)
    setPages(prev => prev[p] && prev[p] !== 'error' ? prev : ({ ...prev, [p]: 'loading' }))
    try {
      const render = await authedFetch('/.netlify/functions/render-document-page', { document_id: doc.id, page: p })
      if (!render.image || (p === 1 && !render.page_count)) throw new Error('Document preview unavailable')
      if (p === 1) setPageCount(render.page_count)
      loadedPages.current.add(p)
      setPages(prev => ({ ...prev, [p]: render.image }))
    } catch {
      setPages(prev => ({ ...prev, [p]: 'error' }))
    } finally { loadingPages.current.delete(p) }
  }

  useEffect(() => {
    if (!pageCount) return
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const p = parseInt(entry.target.dataset.page)
        if (!p) continue
        loadPage(p)
        for (let i = 1; i <= PRELOAD_AHEAD; i++) {
          if (p + i <= pageCount) loadPage(p + i)
        }
      }
    }, { rootMargin: '300px' })
    Object.values(pageRefs.current).forEach(el => { if (el) obs.observe(el) })
    return () => obs.disconnect()
  }, [pageCount])

  const requiredRoles = [...new Set(fields.filter(f => f.field_type === 'signature').map(f => f.signer_role))]
  const signedRoles = [...new Set([...Object.keys(priorSigs), ...Object.keys(sigs)])]
  const allSigned = requiredRoles.length > 0 && requiredRoles.every(r => signedRoles.includes(r))
  const allPagesReady = pageCount > 0 && Array.from({ length: pageCount }, (_, i) => i + 1).every(p => visiblePages[p])

  function handlePadSign(signatureData) {
    if (padRole) setSigs(prev => ({ ...prev, [padRole]: signatureData }))
    setPadRole(null)
  }

  async function submit() {
    if (submitLock.current || !allSigned || !allPagesReady || error) return
    submitLock.current = true
    setSubmitting(true); setSubmitError('')
    try {
      const result = await authedFetch('/.netlify/functions/complete-signing', { document_id: doc.id, signatures: sigs })
      onComplete && onComplete(result)
    } catch (err) { setSubmitError(err.message) }
    finally { submitLock.current = false; setSubmitting(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#202326', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <div style={{ color: '#CBB17B', fontFamily: "'DM Sans',sans-serif", fontSize: 16, fontWeight: 700 }}>{doc.file_name}</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 2 }}>Tap each highlighted box to sign</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {requiredRoles.map(role => {
            const c = ROLE_COLOR[role] || ROLE_COLOR.client
            const done = signedRoles.includes(role)
            return (
              <div key={role} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: done ? '#D1FAE5' : '#F7F3EB', color: done ? '#065F46' : c.label, fontWeight: 600 }}>
                {done ? '✓ ' : ''}{ROLE_TITLE[role]}
              </div>
            )
          })}
          <button aria-label="Close signing document" disabled={submitting} onClick={() => !submitting && onClose()} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 0', background: '#2a2a2a', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        {loading && <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 60 }}>Loading document…</div>}
        {error && !loading && <div style={{ padding: '16px 24px', background: '#FEE2E2', color: '#991B1B', borderRadius: 8, margin: 20, fontSize: 13 }}>{error} <button className="btn btn-ghost" onClick={init}>Try again</button></div>}
        {submitError && <div role="alert" style={{ padding: 16, background: '#FEE2E2', color: '#991B1B' }}>{submitError} Your signature is still here; please try submitting again.</div>}

        {!loading && !error && Array.from({ length: pageCount || 1 }, (_, i) => i + 1).map(p => (
          <div key={p} style={{ width: '100%', maxWidth: 820, padding: '0 16px', boxSizing: 'border-box' }}>
            <div ref={el => { pageRefs.current[p] = el }} data-page={p}
              style={{ position: 'relative', width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.5)', borderRadius: 4, background: '#e8e4dc', minHeight: 200 }}>
              {pages[p] && pages[p] !== 'loading' && pages[p] !== 'error'
                ? <img src={pages[p]} onLoad={() => setVisiblePages(prev => ({ ...prev, [p]: true }))} onError={() => { loadedPages.current.delete(p); setVisiblePages(prev => ({ ...prev, [p]: false })); setPages(prev => ({ ...prev, [p]: 'error' })) }} alt={`Page ${p}`} style={{ width: '100%', display: 'block', borderRadius: 4, userSelect: 'none' }} />
                : <div style={{ width: '100%', paddingBottom: '129%', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {pages[p] === 'error'
                        ? <button className="btn btn-ghost" onClick={() => loadPage(p)}>Could not load page {p}. Try again</button>
                        : <div style={{ width: 28, height: 28, border: '3px solid #555', borderTopColor: '#CBB17B', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />}
                    </div>
                  </div>}
              {visiblePages[p] && fields.filter(f => (f.page_number || 1) === p).map((f, i) => {
                const c = ROLE_COLOR[f.signer_role] || ROLE_COLOR.client
                const roleSigned = signedRoles.includes(f.signer_role)
                const saved = priorSigs[f.signer_role]
                const signatureImage = saved?.signature_data || sigs[f.signer_role]
                const isSig = f.field_type === 'signature'
                return (
                  <div key={i} role={isSig ? 'button' : undefined} tabIndex={isSig && !saved ? 0 : undefined} aria-label={isSig ? `${ROLE_TITLE[f.signer_role]} signature` : undefined}
                    onKeyDown={e => { if (isSig && !saved && !submitting && ['Enter', ' '].includes(e.key)) { e.preventDefault(); setPadRole(f.signer_role) } }}
                    onClick={() => { if (isSig && !saved && !submitting) setPadRole(f.signer_role) }}
                    style={{ position: 'absolute', left: `${f.x_frac*100}%`, top: `${f.y_frac*100}%`, width: `${f.w_frac*100}%`, height: `${f.h_frac*100}%`, border: `2px ${roleSigned?'solid':'dashed'} ${c.border}`, background: roleSigned?'transparent':c.bg, borderRadius: 3, boxSizing: 'border-box', cursor: isSig?'pointer':'default', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {isSig
                      ? (roleSigned
                          ? <img src={signatureImage} alt="sig" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                          : <span style={{ fontSize:10, fontWeight:700, color:c.label }}>✍️ Tap — {ROLE_TITLE[f.signer_role]}</span>)
                      : <span style={{ fontSize:10, color: roleSigned?'#202326':c.label, fontWeight: roleSigned?600:400 }}>
                          {f.field_type==='date' ? (roleSigned ? (saved?.signed_at ? new Date(saved.signed_at).toLocaleDateString('en-US') : todayStr) : 'date') : (f.field_value || f.label || f.field_type)}
                        </span>}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        <div style={{ height: 100 }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>

      <div style={{ background: '#202326', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        {!allSigned
          ? <span style={{ fontSize:12, color:'rgba(255,255,255,0.4)' }}>
              {requiredRoles.filter(r=>!signedRoles.includes(r)).map(r=>ROLE_TITLE[r]).join(' & ')} remaining
            </span>
          : <span style={{ fontSize:12, color:'#A7F3D0', fontWeight:600 }}>{allPagesReady ? 'Signatures ready to submit' : 'Review all pages before submitting'}</span>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={() => !submitting && onClose()} disabled={submitting} style={{ background: 'transparent', color:'#fff', borderColor:'rgba(255,255,255,0.4)' }}>Cancel</button>
          <button className="btn btn-gold" onClick={submit} disabled={!allSigned || !allPagesReady || !!error || submitting}>
            {submitting ? 'Finalizing…' : 'Finish & Submit'}
          </button>
        </div>
      </div>

      {padRole && (
        <SignaturePad documentName={`${doc.file_name} — ${ROLE_TITLE[padRole]} signature`} onSign={handlePadSign} onClose={() => setPadRole(null)} />
      )}
    </div>
  )
}
